import type { Dictionary } from './en.js';

/**
 * Khmer dictionary — the DEFAULT language for customers (CLAUDE.md §6.2).
 *
 * Typed as `Dictionary`, so removing or misspelling a key breaks the build.
 * That is deliberate: a half-translated storefront is how a Cambodian shopper
 * decides a product is foreign and leaves.
 *
 * Numbers stay in Latin digits — Khmer numerals are readable but merchants and
 * customers both type prices in Latin, and mixing the two in a price list is
 * worse than either alone.
 */

export const km: Dictionary = {
  common: {
    today: 'ថ្ងៃនេះ',
    add: 'បន្ថែម',
    until: 'ដល់',
    appName: 'TeleStore',
    save: 'រក្សាទុក',
    cancel: 'បោះបង់',
    confirm: 'បញ្ជាក់',
    delete: 'លុប',
    edit: 'កែសម្រួល',
    back: 'ត្រឡប់ក្រោយ',
    next: 'បន្ទាប់',
    done: 'រួចរាល់',
    close: 'បិទ',
    retry: 'ព្យាយាមម្ដងទៀត',
    loading: 'កំពុងផ្ទុក…',
    search: 'ស្វែងរក',
    all: 'ទាំងអស់',
    yes: 'បាទ/ចាស',
    no: 'ទេ',
    optional: 'មិនចាំបាច់',
    required: 'ចាំបាច់',
    copy: 'ចម្លង',
    remove: 'ដកចេញ',
    copied: 'បានចម្លង',
    share: 'ចែករំលែក',
    from: 'ចាប់ពី',
    total: 'សរុប',
    subtotal: 'សរុបរង',
    shipping: 'ថ្លៃដឹកជញ្ជូន',
    free: 'ឥតគិតថ្លៃ',
    quantity: 'ចំនួន',
    price: 'តម្លៃ',
    size: 'ទំហំ',
    color: 'ពណ៌',
    outOfStock: 'អស់ស្តុក',
    lowStock: 'នៅសល់តែ {{count}} ប៉ុណ្ណោះ',
    inStock: 'មានស្តុក',
    empty: 'មិនទាន់មានអ្វីនៅទីនេះទេ',
    sun: 'អា',
    mon: 'ច',
    tue: 'អ',
    wed: 'ព',
    thu: 'ព្រ',
    fri: 'សុ',
    sat: 'ស',
  },

  storefront: {
    shopClosed: 'ហាងនេះមិនទទួលការបញ្ជាទិញឥឡូវនេះទេ',
    chatWithShop: 'ជជែកជាមួយហាង',
    viewCart: 'មើលកន្ត្រក',
    addToCart: 'ដាក់ចូលកន្ត្រក',
    buyNow: 'ទិញឥឡូវ',
    cartEmpty: 'កន្ត្រករបស់អ្នកទទេ',
    continueShopping: 'បន្តទិញទំនិញ',
    checkout: 'បង់ប្រាក់',
    categories: 'ប្រភេទ',
    searchProducts: 'ស្វែងរកទំនិញ',
    noProducts: 'រកមិនឃើញទំនិញទេ',
    selectSize: 'ជ្រើសរើសទំហំ',
    selectColor: 'ជ្រើសរើសពណ៌',
    selectOptions: 'ជ្រើសរើសជម្រើសរបស់អ្នក',
    productUnavailable: 'ទំនិញនេះលែងមានទៀតហើយ',
    referencePrice: '≈ {{amount}}',
    openInTelegram: 'បើកក្នុង Telegram',

    /* §4 F&B */
    modifierRequired: 'ត្រូវជ្រើសរើស',
    modifierOptional: 'តាមចិត្ត',
    modifierUpTo: 'រហូតដល់ {{count}}',
    chooseGroup: 'ជ្រើសរើស {{group}}',
    notSellingNow: 'មិនមានលក់នៅពេលនេះ',
    prepTime: 'រៀបចំប្រហែល {{count}} នាទី',
    pickup: 'មកយកខ្លួនឯង',
    localDelivery: 'ដឹកជញ្ជូន',
    dineIn: 'ញ៉ាំនៅហាង',
    chooseFulfilment: 'អ្នកចង់ទទួលបែបណា?',

    /* §4 services */
    chooseStaff: 'ជ្រើសរើសបុគ្គលិក',
    anyStaff: 'អ្នកណាក៏បាន',
    chooseDate: 'ជ្រើសរើសថ្ងៃ',
    chooseTime: 'ជ្រើសរើសម៉ោង',
    bookNow: 'កក់ម៉ោង',
    slotsClosed: 'ថ្ងៃនេះបិទ',
    slotsFull: 'ពេញហើយ — សូមសាកល្បងថ្ងៃផ្សេង',
    slotsPast: 'ថ្ងៃនេះកន្លងផុតទៅហើយ',
    depositNotice: 'ប្រាក់កក់ជួយរក្សាម៉ោងរបស់អ្នក។ ម៉ោងនឹងត្រូវបានបញ្ជាក់ពេលទទួលបានប្រាក់។',
    appointment: 'ការណាត់ជួប',
  },

  checkout: {
    title: 'បញ្ជាក់ការបញ្ជាទិញ',
    yourDetails: 'ព័ត៌មានរបស់អ្នក',
    name: 'ឈ្មោះពេញ',
    phone: 'លេខទូរស័ព្ទ',
    deliveryAddress: 'អាសយដ្ឋានដឹកជញ្ជូន',
    province: 'ខេត្ត/ក្រុង',
    district: 'ស្រុក/ខណ្ឌ',
    addressLine: 'ផ្លូវ លេខផ្ទះ ទីតាំងសម្គាល់',
    note: 'សម្គាល់សម្រាប់ហាង',
    paymentMethod: 'វិធីបង់ប្រាក់',
    payByTransfer: 'ផ្ទេរប្រាក់ (KHQR)',
    payByTransferHint: 'ស្កេនដោយកម្មវិធីធនាគារណាក៏បាន។ ហាងនឹងបញ្ជាក់ពេលទទួលបានប្រាក់។',
    payOnDelivery: 'បង់ប្រាក់ពេលទទួលទំនិញ',
    payOnDeliveryHint: 'បង់ប្រាក់ជូនអ្នកដឹកជញ្ជូននៅពេលទំនិញមកដល់។',
    placeOrder: 'បញ្ជាទិញ',
    placing: 'កំពុងបញ្ជាទិញ…',
    shippingUnavailable: 'ហាងមិនទាន់ដឹកជញ្ជូនទៅខេត្តនេះទេ',
    selectProvinceFirst: 'ជ្រើសរើសខេត្តដើម្បីមើលថ្លៃដឹកជញ្ជូន',
    phoneInvalid: 'សូមបញ្ចូលលេខទូរស័ព្ទកម្ពុជា ឧ. 012 345 678',
    nameRequired: 'សូមបញ្ចូលឈ្មោះរបស់អ្នក',
    addressRequired: 'សូមបញ្ចូលអាសយដ្ឋានដឹកជញ្ជូន',
    promoCode: 'កូដបញ្ចុះតម្លៃ',
    promoPlaceholder: 'បញ្ចូលកូដ',
    applyCode: 'ប្រើ',
    promoNotApplied: 'មិនបានប្រើ៖ {{codes}}',
  },

  payment: {
    scanToPay: 'ស្កេនដើម្បីបង់ប្រាក់',
    transferExactly: 'ផ្ទេរឲ្យត្រូវ {{amount}}',
    exactAmountHint: 'ចំនួនទឹកប្រាក់ត្រូវតែដូចគ្នាបេះបិទ — ដើម្បីឲ្យហាងផ្គូផ្គងការផ្ទេរនេះនឹងការបញ្ជាទិញរបស់អ្នក។',
    payTo: 'ផ្ទេរទៅ',
    expiresIn: 'កូដនេះនឹងផុតកំណត់ក្នុងរយៈពេល {{minutes}} នាទី',
    expired: 'កូដបង់ប្រាក់នេះផុតកំណត់ហើយ',
    requestNewCode: 'យកកូដថ្មី',
    awaitingPayment: 'កំពុងរង់ចាំការផ្ទេរប្រាក់របស់អ្នក',
    paymentReceived: 'បានទទួលប្រាក់',
    iHavePaid: 'ខ្ញុំបានផ្ទេរប្រាក់ហើយ',
    iHavePaidHint: 'ហាងនឹងបញ្ជាក់ក្នុងពេលឆាប់ៗ។',
    saveQr: 'រក្សាទុករូប QR',
    codAmountDue: 'បង់ {{amount}} ជូនអ្នកដឹកជញ្ជូន',
  },

  order: {
    title: 'ការបញ្ជាទិញ {{code}}',
    yourOrders: 'ការបញ្ជាទិញរបស់អ្នក',
    noOrders: 'អ្នកមិនទាន់បានបញ្ជាទិញពីហាងនេះទេ',
    placedAt: 'បញ្ជាទិញនៅ {{date}}',
    items: 'ទំនិញ',
    trackOrder: 'តាមដានការបញ្ជាទិញ',
    getUpdatesOnTelegram: 'ទទួលព័ត៌មានតាម Telegram',
    getUpdatesHint: 'ចុចម្តង។ យើងនឹងផ្ញើសារពេលទំនិញត្រូវបានដឹកចេញ។',
    orderPlaced: 'បានបញ្ជាទិញរួចរាល់',
    orderPlacedHint: 'ហាងនឹងបញ្ជាក់ការបញ្ជាទិញរបស់អ្នកក្នុងពេលឆាប់ៗ។',
    cancelOrder: 'បោះបង់ការបញ្ជាទិញ',
    reorder: 'បញ្ជាទិញម្ដងទៀត',
    exchangeOf: 'ការប្តូរសម្រាប់ការបញ្ជាទិញ {{code}}',
    status: {
      draft: 'ព្រាង',
      pending_confirm: 'រង់ចាំហាងបញ្ជាក់',
      awaiting_payment: 'រង់ចាំការបង់ប្រាក់',
      paid: 'បានបង់ប្រាក់',
      fulfilling: 'កំពុងរៀបចំ',
      confirmed: 'បានបញ្ជាក់',
      shipped: 'បានដឹកចេញ',
      collected: 'បានទទួលប្រាក់',
      remitted: 'បានផ្ទេរប្រាក់មកហាង',
      scheduled: 'បានកក់',
      no_show: 'មិនបានមក',
      completed: 'បញ្ចប់',
      cancelled: 'បានបោះបង់',
      refunded: 'បានសងប្រាក់វិញ',
    },
  },

  bot: {
    createForMe: 'បង្កើតបូតជូនខ្ញុំ',
    createForMeHint: 'Telegram នឹងសុំឲ្យអ្នកបញ្ជាក់ឈ្មោះ។ អ្នកមិនចាំបាច់ចម្លង token ទេ។',
    openInTelegram: 'បើកក្នុង Telegram',
    orPasteToken: 'ឬបិទភ្ជាប់ token',
    welcome: 'សូមស្វាគមន៍មកកាន់ {{shop}} 👋',
    welcomeHint: 'មើលទំនិញ ពិនិត្យការបញ្ជាទិញ ឬសួរសំណួរមកយើង។',
    openShop: 'បើកហាង',
    myOrders: 'ការបញ្ជាទិញរបស់ខ្ញុំ',
    help: 'ជំនួយ',
    contactShop: 'និយាយជាមួយហាង',
    unknownCommand: 'ខ្ញុំមិនយល់ទេ។ សូមប្រើប៊ូតុងខាងក្រោម។',
    orderStatusPrompt: 'តើអ្នកចង់ពិនិត្យការបញ្ជាទិញណាមួយ?',
    noOrdersFound: 'ខ្ញុំរកមិនឃើញការបញ្ជាទិញរបស់អ្នកនៅក្នុងហាងនេះទេ។',
    shippingFeeTitle: 'ថ្លៃដឹកជញ្ជូន',
    shippingFeeLine: '{{province}} — {{fee}}',
    shippingFeeNone: 'ហាងមិនទាន់បានដាក់ថ្លៃដឹកជញ្ជូនទេ។',
    openingHoursTitle: 'ម៉ោងបើកទ្វារ',
    openNow: 'កំពុងបើក',
    closedNow: 'បិទហើយ',
    returnPolicyTitle: 'គោលការណ៍ប្តូរ/សងវិញ',
    noReturnPolicy: 'ហាងមិនទាន់បានដាក់គោលការណ៍ប្តូរ/សងវិញទេ។',
    faqTitle: 'សំណួរញឹកញាប់',
    escalated: 'ខ្ញុំបានបញ្ជូនសំណួររបស់អ្នកទៅហាងហើយ។ ពួកគេនឹងឆ្លើយតបនៅទីនេះឆាប់ៗ។',
    privacyBlocked: 'អ្នកអាចមើលបានតែការបញ្ជាទិញរបស់អ្នកផ្ទាល់ប៉ុណ្ណោះ។',
  },

  merchant: {
    newOrder: 'ការបញ្ជាទិញថ្មី {{code}}',
    confirmOrder: 'បញ្ជាក់',
    rejectOrder: 'បដិសេធ',
    markPaid: 'សម្គាល់ថាបានបង់ប្រាក់',
    markShipped: 'សម្គាល់ថាបានដឹកចេញ',
    markCollected: 'បានទទួលប្រាក់',
    markRemitted: 'អ្នកដឹកបានផ្ទេរប្រាក់មកវិញ',
    markCompleted: 'បញ្ចប់',
    viewOrder: 'មើលការបញ្ជាទិញ',
    todaySummary: 'ថ្ងៃនេះ៖ {{orders}} ការបញ្ជាទិញ · {{revenue}}',
    lowStockTitle: 'ស្តុកជិតអស់',
    lowStockLine: '{{product}} — នៅសល់ {{count}}',
    paymentMatched: 'បានផ្គូផ្គងការបង់ប្រាក់ជាមួយការបញ្ជាទិញ {{code}}',
    paymentUnmatched: 'មានការផ្ទេរប្រាក់ {{amount}} តែរកមិនឃើញការបញ្ជាទិញត្រូវគ្នា',
    paymentUnmatchedHint: 'ចុចលើការបញ្ជាទិញខាងក្រោមដើម្បីភ្ជាប់ ឬមិនអើពើ។',
    listenerBroken: 'ខ្ញុំលែងអានក្រុមជូនដំណឹង ABA របស់អ្នកបានទៀតហើយ',
    listenerBrokenHint: 'សូមប្រាកដថាបូតនៅតែជាអ្នកគ្រប់គ្រង (admin) ក្នុងក្រុមនោះ។',
  },

  notification: {
    'order.placed': 'អរគុណ! ការបញ្ជាទិញ {{code}} បានទទួលហើយ។ សរុប {{total}}។ ហាងនឹងបញ្ជាក់ឆាប់ៗ។',
    'order.awaiting_payment': 'ការបញ្ជាទិញ {{code}} បានបញ្ជាក់ហើយ។ សូមផ្ទេរឲ្យត្រូវ {{amount}} តាមកូដ QR ខាងលើ។',
    'order.payment_reminder':
      'រំលឹក៖ ការបញ្ជាទិញ {{code}} កំពុងរង់ចាំការផ្ទេរ {{amount}}។ កូដនឹងផុតកំណត់ក្នុង {{minutes}} នាទី។',
    'order.paid': 'យើងបានទទួលប្រាក់សម្រាប់ការបញ្ជាទិញ {{code}} ហើយ។ អរគុណ!',
    'order.confirmed': 'ការបញ្ជាទិញ {{code}} បានបញ្ជាក់ហើយ។ អ្នកនឹងបង់ {{amount}} ពេលទទួលទំនិញ។',
    'order.shipped': 'ការបញ្ជាទិញ {{code}} បានដឹកចេញហើយ។ {{tracking}}',
    'order.completed': 'ការបញ្ជាទិញ {{code}} បានបញ្ចប់។ អរគុណដែលបានទិញនៅ {{shop}}!',
    'order.cancelled': 'ការបញ្ជាទិញ {{code}} ត្រូវបានបោះបង់។ {{reason}}',
    'order.refunded': 'បានសងប្រាក់ {{amount}} សម្រាប់ការបញ្ជាទិញ {{code}} វិញហើយ។',
    'order.exchange_created': 'បានបង្កើតការប្តូរ {{code}} សម្រាប់ការបញ្ជាទិញដើម {{original}}។',
    'merchant.new_order': 'ការបញ្ជាទិញថ្មី {{code}} — {{total}} ({{payment}})',
    'merchant.payment_received': 'ទទួលបានប្រាក់ {{amount}} សម្រាប់ការបញ្ជាទិញ {{code}}',
    'merchant.payment_unmatched': 'ការផ្ទេរប្រាក់មិនត្រូវគ្នា៖ {{amount}} ពី {{payer}}',
    'merchant.low_stock': 'ស្តុកជិតអស់៖ {{product}} (នៅសល់ {{count}})',
    'merchant.cod_collected': 'អ្នកដឹកជញ្ជូនបានទទួល {{amount}} សម្រាប់ការបញ្ជាទិញ {{code}}',
    'merchant.staff_joined': '{{name}} បានចូលរួមហាងរបស់អ្នកជា {{role}}',
    'merchant.subscription_reminder': 'គម្រោង {{plan}} របស់អ្នកនឹងបន្តក្នុង {{days}} ថ្ងៃ។ ចុចដើម្បីបង់ {{amount}}។',
    'merchant.subscription_expired':
      'ការជាវរបស់អ្នកបានផុតកំណត់។ ហាងរបស់អ្នកនៅតែដំណើរការ ប៉ុន្តែមុខងារពិសេសត្រូវបានចាក់សោរហូតដល់អ្នកបន្តជាវ។',
    'merchant.credits_low': 'អ្នកបានប្រើ 80% នៃ credits ខែនេះហើយ (នៅសល់ {{remaining}})។',
    'merchant.credits_exhausted': 'អ្នកអស់ credits ហើយ។ ចុចដើម្បីបញ្ចូលបន្ថែម។',
    'merchant.ai_job_done': '{{job}} របស់អ្នករួចរាល់ហើយ។',
    'merchant.provider_degraded':
      '{{provider}} មិនឆ្លើយតបទេ។ ការបង់ប្រាក់បានប្តូរទៅ KHQR ដើម្បីកុំឲ្យការបញ្ជាទិញជាប់គាំង។',
    'merchant.aba_listener_broken':
      'ខ្ញុំលែងអានក្រុមជូនដំណឹង ABA បានទៀតហើយ — ការបង់ប្រាក់ត្រូវបញ្ជាក់ដោយដៃរហូតដល់ជួសជុលរួច។',
    'customer.win_back': 'យើងនឹករលឹកអ្នក! {{shop}} មានទំនិញថ្មីមកដល់ហើយ សូមអញ្ជើញមើល។',
    'ops.exchange_rate_stale':
      'អត្រាប្តូរប្រាក់ USD/KHR មិនបានធ្វើបច្ចុប្បន្នភាព {{days}} ថ្ងៃហើយ។ នៅតែបង្ហាញ {{rate}} ពី {{effectiveOn}}។',
    'booking.reminder_24h':
      'រំលឹក៖ ការណាត់ជួបនៅ {{shop}} គឺថ្ងៃស្អែក {{when}}។ {{service}} ជាមួយ {{who}}។',
    'booking.reminder_2h':
      'ការណាត់ជួបនៅ {{shop}} គឺម៉ោង {{when}} — នៅប្រមាណ ២ ម៉ោងទៀត។ {{service}} ជាមួយ {{who}}។',
    'booking.rescheduled': 'ការណាត់ជួបនៅ {{shop}} ត្រូវបានផ្លាស់ទៅ {{when}}។',
    'ops.new_lead': 'អតិថិជនថ្មី៖ {{name}} · {{contact}} · លក់ {{sells}} · តាម {{source}}',
  },

  admin: {
    planFree: 'ឥតគិតថ្លៃ',
    shopName: 'ឈ្មោះហាង',
    integrationsTitle: 'ភ្ជាប់ជាមួយ',
    createShopHeading: 'បង្កើតហាងរបស់អ្នក',
    shopNamePlaceholder: 'ឧ. ហាងសុខា',
    createShopFooter: 'បន្ថែមទំនិញ និងភ្ជាប់បូតបន្ទាប់ពីនេះ — គ្រប់យ៉ាងប្តូរបាន លើកលែងតែប្រភេទអ្វីដែលអ្នកលក់។',
    listingCurrency: 'ដាក់តម្លៃទំនិញជា',
    listingCurrencyHint:
      'រូបិយប័ណ្ណមួយទៀតបង្ហាញក្បែរតម្លៃសរុបជានិច្ច តាមអត្រាផ្លូវការរបស់ធនាគារជាតិនៃកម្ពុជា ដែលធ្វើបច្ចុប្បន្នភាពរៀងរាល់ថ្ងៃ អ្នកមិនបាច់វាយដោយខ្លួនឯងទេ។ អ្នកអាចប្តូរពេលក្រោយបាន។',
    verticalFixedHint: 'មិនអាចប្តូរពេលក្រោយបានទេ។',
    whatDoYouSell: 'តើអ្នកលក់អ្វី?',
    verticalRetail: 'ទំនិញដែលត្រូវដឹកជញ្ជូន (សម្លៀកបំពាក់ គ្រឿងសម្អាង)',
    verticalFnb: 'ម្ហូប និងភេសជ្ជៈ',
    verticalServices: 'ការណាត់ជួប (ហាងកាត់សក់ គ្លីនិក)',
    verticalDigital: 'កូដ និងប័ណ្ណ (បញ្ចូលលុយហ្គេម ប័ណ្ណអំណោយ សៀវភៅឌីជីថល)',
    codes: 'កូដ',
    codesInStock: 'នៅសល់',
    codesUndelivered: 'បង់ប្រាក់ហើយ តែមិនទាន់ផ្ញើ',
    codesSold: 'បានលក់',
    codesLow: 'ជិតអស់',
    codesPaste: 'បិទភ្ជាប់កូដ មួយបន្ទាត់មួយ',
    codesAdd: 'បញ្ចូលទៅស្តុក',
    codesAdded: 'បានបញ្ចូល {{count}} កូដ',
    codesInstructions: 'របៀបប្រើកូដ',
    codesInstructionsHint: 'ផ្ញើភ្ជាប់ជាមួយរាល់កូដ។ ប្រើនៅឯណា និងមុនពេលណា។',
    codesLowThreshold: 'ជូនដំណឹងខ្ញុំពេលនៅតិចជាង',
    codesEmpty: 'មិនទាន់មានទំនិញ។ បង្កើតមួយសិន រួចបិទភ្ជាប់កូដនៅទីនេះ។',
    dashboard: 'ផ្ទាំងគ្រប់គ្រង',
    orders: 'ការបញ្ជាទិញ',
    products: 'ទំនិញ',
    customers: 'អតិថិជន',
    settings: 'ការកំណត់',
    team: 'ក្រុមការងារ',
    teamJoined: 'អ្នកបានចូលរួម {{shop}} ជា {{role}} ហើយ។ បើកផ្ទាំងគ្រប់គ្រងដើម្បីចាប់ផ្តើម។',
    billing: 'វិក្កយបត្រ',
    theme: 'រូបរាង',
    delivery: 'ការដឹកជញ្ជូន',
    payments: 'ការបង់ប្រាក់',
    support: 'ជំនួយអតិថិជន',
    signIn: 'ចូលដោយ Telegram',
    signOut: 'ចាកចេញ',
    createShopIntro:
      'ចាប់ផ្តើមឥតគិតថ្លៃ មិនត្រូវការកាត។ ទទួលប្រាក់តាម KHQR ដែលកម្មវិធីធនាគារណាក៏ស្កេនបាន រួមទាំង ABA ព្រមទាំងជំនួយការ AI ឆ្លើយសំណួរអតិថិជនជំនួសអ្នក។',
    createShop: 'បង្កើតហាង',
    revenue: 'ចំណូល',
    ordersToday: 'ការបញ្ជាទិញថ្ងៃនេះ',
    pendingConfirm: 'រង់ចាំបញ្ជាក់',
    awaitingPayment: 'រង់ចាំការបង់ប្រាក់',
    readyToShip: 'រួចរាល់ដឹកចេញ',
    addProduct: 'បន្ថែមទំនិញ',
    importFromFacebook: 'នាំចូលពី Facebook',
    replyKit: 'ចម្លងសារឆ្លើយតបសម្រាប់ Facebook',
    variants: 'ជម្រើស',
    addVariant: 'បន្ថែមជម្រើស',
    stock: 'ស្តុក',
    publish: 'បង្ហោះ',
    unpublish: 'ដកចេញ',
    shops: 'ហាង',
    creditsShared: 'គម្រោង និង credits របស់អ្នក ប្រើរួមគ្នាសម្រាប់គ្រប់ហាងទាំងអស់។',
    credits: 'Credits',
    creditsRemaining: 'នៅសល់ {{count}} credits',
    topUp: 'បញ្ចូលបន្ថែម',
    plan: 'គម្រោង',
    changePlan: 'ប្តូរគម្រោង',
    upgradeToUnlock: 'ដំឡើងគម្រោងដើម្បីដោះសោ',
    inviteStaff: 'អញ្ជើញបុគ្គលិក',
    inviteLink: 'ចែករំលែកតំណនេះជូនបុគ្គលិករបស់អ្នក',
    inviteInvalid: 'សូមស្នើម្ចាស់ហាងផ្ញើតំណអញ្ជើញថ្មីជូនអ្នក។',
    staffCapHint: 'គម្រោងរបស់អ្នកមានសមាជិក {{count}} នាក់។ ដំឡើងគម្រោងដើម្បីបន្ថែម។',
    upgrade: 'ដំឡើងគម្រោង',
    planActivated: 'ទទួលបានការបង់ប្រាក់ — គម្រោង {{plan}} របស់អ្នកកំពុងដំណើរការហើយ។',
    creditsAdded: 'ទទួលបានការបង់ប្រាក់ — បានបន្ថែម {{credits}} ក្រេឌីតទៅសមតុល្យរបស់អ្នក។',
    payToUpgrade: 'ស្កេនដើម្បីបង់ {{amount}}',
    payToUpgradeHint:
      'សូមផ្ទេរចំនួនទឹកប្រាក់ឲ្យត្រូវជាក់លាក់ — លេខខ្ទង់ចុងក្រោយជាអ្វីដែលយើងប្រើសម្គាល់ការបង់ប្រាក់របស់អ្នក។ គម្រោងនឹងដំណើរការក្នុងរយៈពេលប៉ុន្មាននាទី។',
    chargePending: 'កំពុងរង់ចាំការផ្ទេរប្រាក់របស់អ្នក',
    removeMember: 'ដកចេញ',
    khqrSetup: 'ការរៀបចំ KHQR',
    khqrSetupHint: 'បញ្ចូល ABA account id ដែលបង្ហាញក្នុងកម្មវិធីធនាគាររបស់អ្នក។ អតិថិជនអាចបង់ KHQR នេះពីកម្មវិធីធនាគារណាក៏បាន។',
    abaAccountId: 'លេខសម្គាល់ធនាគារ',
    abaAccountIdHint: 'បំពេញស្រាប់សម្រាប់ ABA។ ប្តូរតែពេលអ្នកប្រើធនាគារផ្សេង។',
    abaAccountNumber: 'លេខគណនី ABA របស់អ្នក',
    abaAccountNumberHint: 'លេខនៅលើកាត ABA QR។ នេះជាកន្លែងដែលប្រាក់ចូល។',
    khqrUpload: 'ផ្ទុករូប ABA/KHQR របស់អ្នកនៅទីនេះ',
    khqrUploadHint:
      'ជ្រើសរើសរូបកាត QR ទទួលប្រាក់របស់អ្នក។ យើងអានព័ត៌មានចេញពីវា — រូបមិនចាកចេញពីឧបករណ៍នេះទេ។',
    khqrScanning: 'កំពុងអានកូដ…',
    khqrManualInstead: 'បញ្ចូលព័ត៌មានដោយដៃជំនួសវិញ',
    khqrConfirmTitle: 'ប្រាក់នឹងចូលក្នុងគណនីនេះ',
    khqrConfirmSave: 'ត្រឹមត្រូវហើយ — រក្សាទុក',
    khqrRetake: 'ថតរូបម្តងទៀត',
    khqrNotAba:
      'អ្នកអាចចាប់ផ្តើមលក់បានភ្លាម។ ការផ្ទៀងផ្ទាត់ការបង់ប្រាក់ស្វ័យប្រវត្តិដំណើរការតែជាមួយ ABA ប៉ុណ្ណោះសម្រាប់ពេលនេះ ដូច្នេះសូមសម្គាល់ការផ្ទេរប្រាក់ដោយខ្លួនឯង។',
    khqrErrNoCode: 'រកមិនឃើញកូដ QR ទេ។ សូមចូលឲ្យជិត រកកន្លែងភ្លឺ ហើយដាក់កូដនៅកណ្តាលស៊ុម។',
    khqrErrNotKhqr: 'រូបនោះមានកូដ QR ប៉ុន្តែមិនមែនជាកូដបង់ប្រាក់ទេ។',
    khqrErrChecksum: 'អានកូដមិនច្បាស់។ សូមថតរូបម្តងទៀត។',
    khqrErrNoAccount:
      'កូដនេះគ្មានលេខគណនីទេ ដូច្នេះប្រាក់ដែលបង់នឹងមិនទៅដល់ឡើយ។ សូមប្រើ QR ទទួលប្រាក់ក្នុងកម្មវិធីធនាគាររបស់អ្នក ឬបញ្ចូលដោយដៃ។',
    khqrErrNoLatinName:
      'កូដនេះមានតែឈ្មោះជាភាសាខ្មែរ។ សូមបញ្ចូលឈ្មោះជាអក្សរឡាតាំងដោយដៃ — វាជាអ្វីដែលបង្ហាញក្នុងកម្មវិធីធនាគាររបស់អតិថិជន។',
    abaGroupSetup: 'ក្រុមជូនដំណឹង ABA',
    abaGroupHint:
      'បញ្ចូលបូតនេះទៅក្នុងក្រុមជូនដំណឹង ABA របស់អ្នក ហើយតែងតាំងជា admin។ យើងអានតែប៉ុណ្ណោះ មិនផ្ញើសារទេ។',
    exportData: 'នាំចេញទិន្នន័យទាំងអស់ (CSV)',
    deleteBot: 'លុបបូតរបស់ខ្ញុំ',
  },

  booking: {
    chooseTime: 'ជ្រើសម៉ោង',
    chooseWith: 'ជាមួយនរណា?',
    anyAvailable: 'អ្នកណាក៏បាន',
    noSlots: 'ថ្ងៃនោះមិនមានម៉ោងទេ',
    closedThatDay: 'ថ្ងៃនោះបិទ',
    fullyBooked: 'ថ្ងៃនោះកក់ពេញហើយ',
    pickAnotherDay: 'សូមជ្រើសថ្ងៃផ្សេង',
    yourAppointment: 'ការណាត់ជួបរបស់អ្នក',
    reschedule: 'ផ្លាស់ម៉ោង',
    cancelBooking: 'បោះបង់',
    rescheduled: 'ការណាត់ជួបរបស់អ្នកត្រូវបានផ្លាស់ហើយ',
    slotTaken: 'មានអ្នកកក់ម៉ោងនោះមុនហើយ។ សូមជ្រើសម៉ោងផ្សេង។',
    reminderTomorrow: 'រំលឹក៖ អ្នកមានការណាត់ជួបនៅ {{shop}} ថ្ងៃស្អែក។',
    reminderSoon: 'ការណាត់ជួបរបស់អ្នកនៅ {{shop}} នឹងមកដល់ក្នុងពេលប្រមាណ ២ ម៉ោងទៀត។',
    depositHolds: 'សូមផ្ទេរប្រាក់កក់ក្នុងរយៈពេល {{minutes}} នាទី ដើម្បីរក្សាម៉ោងនេះ។',
    bookedOnTrust: 'មិនចាំបាច់បង់ប្រាក់កក់ទេ — គ្រាន់តែមកតាមម៉ោង។',
    duration: '{{minutes}} នាទី',
  },

  fnb: {
    menu: 'ម៉ឺនុយ',
    sellingHoursAllDay: 'ពេញមួយថ្ងៃ',
    soldOutBoard: 'អស់ស្តុកថ្ងៃនេះ',
    soldOutHint: 'ចុចលើមុខម្ហូបដើម្បីដកចេញពីម៉ឺនុយ។ ចុចម្តងទៀតដើម្បីដាក់ត្រឡប់វិញ។',
    modifierGroupsHint: 'ក្រុមជម្រើស — កម្រិតស្ករ ទំហំ ឬចំណីបន្ថែម។ សរសេរម្តង រួចភ្ជាប់ទៅមុខម្ហូបប៉ុន្មានក៏បាន។',
    addOption: 'បន្ថែមជម្រើស',
    isAvailable: 'មាន',
    chooseOptions: 'ជ្រើសរើសជម្រើស',
    required: 'ចាំបាច់',
    pickUpTo: 'ជ្រើសបានអតិបរមា {{count}}',
    soldOut: 'អស់ហើយ',
    notServedNow: 'មិនមានលក់នៅពេលនេះ',
    pickup: 'មកយកផ្ទាល់',
    localDelivery: 'ដឹកជញ្ជូន',
    dineIn: 'ញាំនៅហាង',
    howToGetIt: 'អ្នកចង់ទទួលយ៉ាងណា?',
    whenToCollect: 'មកយកពេលណា?',
    asSoonAsPossible: 'ក្នុងពេលឆាប់បំផុត',
    readyIn: 'រួចរាល់ក្នុងប្រមាណ {{minutes}} នាទី',
    deliveryMinimum: 'មិនតិចជាង {{amount}} ទើបដឹកជញ្ជូន',
    kitchenTicket: 'សន្លឹកចម្អិន',
    markSoldOut: 'អស់ហើយ',
    markAvailable: 'មានវិញ',
    restockAll: 'មានទាំងអស់វិញ',
    modifierGroups: 'ក្រុមជម្រើស',
    newGroup: 'បង្កើតក្រុមជម្រើស',
    groupName: 'ឈ្មោះក្រុម',
    selectionType: 'អាចជ្រើសបានប៉ុន្មាន?',
    pickOne: 'តែមួយ',
    pickMany: 'ច្រើន',
    minSelect: 'យ៉ាងតិច',
    maxSelect: 'យ៉ាងច្រើន',
    optionName: 'ជម្រើស',
    priceDelta: 'គិតថ្លៃបន្ថែម',
    isDefault: 'លំនាំមុន',
    prepMinutes: 'រយៈពេលរៀបចំ (នាទី)',
    sellingHours: 'លក់តែម៉ោងជាក់លាក់',
    attachedItems: 'ប្រើដោយ {{count}} មុខ',
  },

  resources: {
    staffHint: 'អ្នកដែលអាចត្រូវបានកក់។ ម៉ោងធ្វើការរបស់គេកំណត់ម៉ោងដែលអតិថិជនឃើញ។',
    blockHint: 'ពេលសម្រាក ឬថ្ងៃឈប់។ អតិថិជនមិនអាចកក់ពេលនោះបានទេ។',
    dayOff: 'ថ្ងៃឈប់',
    inactive: 'មិនសកម្ម',
    deactivate: 'បិទដំណើរការ',
    daysPerWeek: '{{count}} ថ្ងៃក្នុងមួយសប្តាហ៍',
    noHours: 'មិនទាន់កំណត់ម៉ោង',
    title: 'អ្នកដែលទទួលការកក់',
    newResource: 'បន្ថែមមនុស្ស',
    resourceName: 'ឈ្មោះ',
    linkedStaff: 'បុគ្គលិក',
    notLinked: 'មិនមែនបុគ្គលិក',
    workingHours: 'ម៉ោងធ្វើការ',
    addWindow: 'បន្ថែមម៉ោង',
    calendar: 'ប្រតិទិន',
    blockTime: 'បិទម៉ោង',
    blocked: 'បានបិទ',
    noAppointments: 'មិនមានការណាត់ជួបក្នុងអំឡុងពេលនេះ',
    serviceSettings: 'ការកំណត់ការកក់',
    durationMinutes: 'រយៈពេលធ្វើ (នាទី)',
    bufferMinutes: 'ចន្លោះពេលក្រោយ (នាទី)',
    depositAmount: 'ប្រាក់កក់រក្សាម៉ោង',
    noDeposit: 'មិនយកប្រាក់កក់',
    whoCanDoIt: 'អ្នកណាអាចធ្វើបាន',
    anyone: 'អ្នកណាក៏បាន',
  },

  trust: {
    shopSuspended: 'ហាងនេះមិនទទួលការបញ្ជាទិញទេ',
    shopSuspendedHint:
      'TeleStore បានផ្អាកហាងនេះជាបណ្ណោះអាសន្ន ខណៈពិនិត្យការរាយការណ៍មួយ។ ការបញ្ជាទិញដែលអ្នកធ្វើរួចហើយ មិនមានផលប៉ះពាល់ទេ។',
    reportShop: 'រាយការណ៍ហាងនេះ',
    reportHint:
      'ប្រាប់យើងបើហាងនេះលក់របស់ដែលមិនគួរលក់ ឬយកប្រាក់ហើយមិនផ្ញើទំនិញ។ អ្នកមិនចាំបាច់មានគណនីទេ។',
    reportReason: 'មានបញ្ហាអ្វី?',
    reportDetail: 'មានអ្វីកើតឡើង? (មិនចាំបាច់)',
    reportContact: 'យើងអាចទាក់ទងអ្នកយ៉ាងដូចម្តេច? (មិនចាំបាច់)',
    reportSubmit: 'ផ្ញើការរាយការណ៍',
    reportThanks: 'អរគុណ។ នឹងមានមនុស្សអានវា។',

    policy: 'គោលការណ៍',
    prohibitedGoods: 'អ្វីដែលអ្នកមិនអាចលក់',
    prohibitedIntro:
      'ហាងលើ TeleStore មិនអាចលក់របស់ទាំងនេះទេ។ បើមិនច្បាស់ សូមសួរយើងមុននឹងចុះផ្សាយ។',
    terms: 'លក្ខខណ្ឌប្រើប្រាស់',
    privacy: 'គោលការណ៍ឯកជនភាព',
    accept: 'ខ្ញុំបានអាន និងយល់ព្រម',
    accepted: 'យល់ព្រមនៅ {{date}}',
    acceptAll: 'យល់ព្រម និងបន្ត',
    outstanding: 'សូមអាន និងយល់ព្រមរឿងទាំងនេះ',
    outstandingHint:
      'ហាងទាំងអស់យល់ព្រមរឿងនេះមុននឹងបើកហាង។ ចំណាយពេលតែមួយនាទី ហើយធ្វើតែម្ដងគត់។',
    updated: 'ឯកសារនេះបានផ្លាស់ប្តូរ ចាប់តាំងពីអ្នកយល់ព្រម',
    returnTemplate: 'គោលការណ៍ប្តូរ/សងវិញ',
    returnTemplateHint:
      'ជ្រើសមួយដើម្បីចាប់ផ្ដើម។ វានឹងត្រូវចម្លងទៅក្នុងការកំណត់របស់អ្នក ហើយអ្នកអាចកែបានក្រោយមក។',
    adoptTemplate: 'ប្រើឯកសារនេះ',
    currentReturnPolicy: 'គោលការណ៍បច្ចុប្បន្នរបស់អ្នក',
    reportsAboutYou: 'ការរាយការណ៍អំពីហាងរបស់អ្នក',
    noReports: 'គ្មានអ្នកណារាយការណ៍ហាងរបស់អ្នកទេ។',
    moderationHistory: 'សេចក្តីសម្រេច',
    suspendedBanner: 'ហាងរបស់អ្នកត្រូវបានផ្អាក',
    suspendedBannerHint:
      'ហាងរបស់អ្នកបានឈប់ទទួលការបញ្ជាទិញ។ ទិន្នន័យ និងការបញ្ជាទិញចាស់មិនមានផលប៉ះពាល់ទេ — អ្នកនៅតែអាចនាំចេញទិន្នន័យទាំងអស់បាន។ សូមសរសេរមកយើងតាម Telegram បើអ្នកគិតថានេះមិនត្រឹមត្រូវ។',
  },

  growth: {
    assistant: 'ការជជែកផ្ទាល់ខ្លួន',
    assistantIntro:
      'ឆ្លើយតបអតិថិជនក្នុង Telegram ផ្ទាល់ខ្លួនរបស់អ្នក មិនមែនតែក្នុងបូតហាងទេ។',
    assistantSetup:
      'បើក Telegram → Settings → Telegram Business → Chat Automation រួចជ្រើសរើស {{bot}}។',
    assistantConnected: 'បានភ្ជាប់',
    assistantNotConnected: 'មិនទាន់បានភ្ជាប់ទេ',
    assistantNoReply:
      'បានភ្ជាប់ ប៉ុន្តែមិនមានសិទ្ធិឆ្លើយទេ។ សូមអនុញ្ញាតក្នុង Telegram។',
    assistantMode: 'របៀបឆ្លើយ',
    assistantModeDraft: 'ស្នើសុំមកខ្ញុំជាមុន',
    assistantModeAuto: 'ឆ្លើយទៅអតិថិជនដោយផ្ទាល់',
    assistantTakeover:
      'នៅពេលអ្នកឆ្លើយដោយខ្លួនអ្នក ជំនួយការនឹងឈប់ឆ្លើយក្នុងការជជែកនោះ។',
    marketing: 'ទីផ្សារ',
    analytics: 'របាយការណ៍',
    promotions: 'ការបញ្ចុះតម្លៃ',
    newPromotion: 'បង្កើតការបញ្ចុះតម្លៃ',
    promoName: 'ឈ្មោះយុទ្ធនាការ',
    promoCodeOptional: 'កូដ (ទុកទទេ ដើម្បីអនុវត្តស្វ័យប្រវត្តិ)',
    discountType: 'ប្រភេទបញ្ចុះតម្លៃ',
    percentOff: 'បញ្ចុះជាភាគរយ',
    fixedOff: 'បញ្ចុះជាចំនួនទឹកប្រាក់',
    freeShipping: 'ដឹកជញ្ជូនឥតគិតថ្លៃ',
    discountValue: 'ចំនួន',
    maxDiscount: 'កំណត់អតិបរមា',
    minSubtotal: 'ការបញ្ជាទិញអប្បបរមា',
    startsAt: 'ចាប់ផ្ដើម',
    endsAt: 'បញ្ចប់',
    customerSegment: 'អនុវត្តចំពោះនរណា',
    segmentAll: 'អតិថិជនទាំងអស់',
    segmentNew: 'អតិថិជនថ្មី',
    segmentReturning: 'អតិថិជនចាស់',
    usageLimitTotal: 'ចំនួនប្រើសរុប',
    usageLimitPerCustomer: 'ចំនួនប្រើក្នុងម្នាក់',
    priority: 'អាទិភាព',
    active: 'កំពុងដំណើរការ',
    paused: 'បានផ្អាក',
    redemptions: 'បានប្រើ {{count}} ដង',
    discountGranted: 'បានបញ្ចុះសរុប',
    copyShareLink: 'ចម្លងតំណចែករំលែក',
    deactivate: 'បិទដំណើរការ',
    noPromotions: 'មិនទាន់មានការបញ្ចុះតម្លៃទេ',
    noPromotionsHint:
      'កូដមួយដែលអ្នកអាចដាក់ក្នុងផុសហ្វេសប៊ុក គឺជាយុទ្ធនាការដែលចំណាយតិចបំផុត។',
    stackingNote:
      'ការបញ្ចុះតម្លៃមួយប៉ុណ្ណោះក្នុងមួយការបញ្ជាទិញ។ កូដដែលអតិថិជនវាយបញ្ចូល មានអាទិភាពជាងការបញ្ចុះស្វ័យប្រវត្តិ។',

    affiliates: 'ដៃគូបញ្ចូលអតិថិជន',
    newAffiliate: 'បន្ថែមដៃគូ',
    affiliateName: 'ឈ្មោះ',
    affiliateCode: 'កូដណែនាំ',
    commissionRate: 'កំរៃជើងសារ',
    minPayout: 'ចំនួនអប្បបរមាដើម្បីទូទាត់',
    referralLink: 'តំណណែនាំ',
    commissions: 'កំរៃជើងសារ',
    commissionPending: 'កំពុងរង់ចាំ',
    commissionApproved: 'អាចទូទាត់បាន',
    commissionPaid: 'បានទូទាត់',
    payOut: 'កត់ត្រាការទូទាត់',
    payoutReference: 'លេខយោងការផ្ទេរ',
    noAffiliates: 'មិនទាន់មានដៃគូទេ',
    noAffiliatesHint:
      'ផ្តល់តំណមួយឲ្យអ្នកលក់បន្ត ហើយគាត់ទទួលបានកំរៃពីរាល់ការបញ្ជាទិញ។',
    returnWindowNote:
      'កំរៃជើងសារអាចទូទាត់បាន {{days}} ថ្ងៃក្រោយការបញ្ជាទិញបញ្ចប់។',

    broadcasts: 'សារផ្សាយ',
    newBroadcast: 'បង្កើតសារផ្សាយ',
    broadcastName: 'ឈ្មោះខាងក្នុង',
    broadcastBody: 'អត្ថបទសារ',
    broadcastButton: 'ឈ្មោះប៊ូតុង',
    broadcastButtonUrl: 'តំណប៊ូតុង',
    audience: 'អ្នកទទួល',
    segmentWithOrders: 'អតិថិជនធ្លាប់បញ្ជាទិញ',
    segmentNoOrders: 'អតិថិជនមិនធ្លាប់បញ្ជាទិញ',
    segmentRecent: 'បញ្ជាទិញក្នុង 60 ថ្ងៃចុងក្រោយ',
    segmentDormant: 'ស្ងាត់លើសពី 60 ថ្ងៃ',
    recipients: 'អតិថិជន {{count}} នាក់',
    sendNow: 'ផ្ញើឥឡូវ',
    sending: 'កំពុងផ្ញើ...',
    sentSummary: 'ផ្ញើ {{sent}} - ទប់ស្កាត់ {{blocked}} - បរាជ័យ {{failed}}',
    cancelBroadcast: 'បញ្ឈប់',
    noBroadcasts: 'មិនទាន់មានសារផ្សាយទេ',
    pacedNote:
      'សារត្រូវបានផ្ញើបន្តិចម្តងៗ ដើម្បីឲ្យបូតរបស់អ្នកនៅតែឆ្លើយតបអតិថិជនបាន។',

    automation: 'សារស្វ័យប្រវត្តិ',
    paymentReminder: 'រំលឹកអតិថិជនមុនកូដបង់ប្រាក់ផុតកំណត់',
    reminderMinutes: 'រំលឹកមុនផុតកំណត់ប៉ុន្មាននាទី',
    winBack: 'ផ្ញើសារទៅអតិថិជនដែលស្ងាត់យូរ',
    winBackDays: 'ក្រោយពីគ្មានការបញ្ជាទិញប៉ុន្មានថ្ងៃ',
    quietHours: 'ម៉ោងស្ងាត់',
    quietHoursHint: 'គ្មានសារស្វ័យប្រវត្តិត្រូវបានផ្ញើក្នុងចន្លោះម៉ោងនេះទេ។',

    range7: '7 ថ្ងៃចុងក្រោយ',
    range30: '30 ថ្ងៃចុងក្រោយ',
    range90: '90 ថ្ងៃចុងក្រោយ',
    grossRevenue: 'ចំណូល',
    averageOrder: 'ការបញ្ជាទិញជាមធ្យម',
    discountsGiven: 'បានបញ្ចុះតម្លៃ',
    codOutstanding: 'COD មិនទាន់ផ្ទេរមកវិញ',
    codShipped: 'COD បានដឹកចេញ',
    codCollected: 'អ្នកដឹកបានទទួលប្រាក់',
    codRemitted: 'បានផ្ទេរមកអ្នក',
    topProducts: 'ទំនិញលក់ដាច់',
    topVariants: 'ទំហំ/ពណ៌លក់ដាច់',
    acquisition: 'ការបញ្ជាទិញមកពីណា',
    units: 'លក់បាន {{count}}',
    noData: 'គ្មានការបញ្ជាទិញក្នុងអំឡុងពេលនេះ',
  },

  errors: {
    unauthenticated: 'សូមចូលម្តងទៀត',
    forbidden: 'អ្នកមិនមានសិទ្ធិចូលមើលទេ',
    shop_not_found: 'រកមិនឃើញហាង',
    shop_suspended: 'ហាងនេះត្រូវបានផ្អាក',
    not_a_member: 'អ្នកមិនមែនជាសមាជិកនៃហាងនេះទេ',
    validation_failed: 'សូមពិនិត្យប្រអប់ដែលបានបន្លិច',
    not_found: 'រកមិនឃើញ',
    conflict: 'មានទិន្នន័យស្ទួនរួចហើយ',
    slug_taken: 'អាសយដ្ឋានហាងនេះមានគេប្រើហើយ',
    product_unavailable: 'ទំនិញនេះលែងមានទៀតហើយ',
    variant_unavailable: 'ជម្រើសនេះលែងមានទៀតហើយ',
    out_of_stock: 'ស្តុកមិនគ្រប់គ្រាន់',
    invalid_transition: 'សកម្មភាពនេះមិនអាចធ្វើបានសម្រាប់ការបញ្ជាទិញនេះឥឡូវទេ',
    order_locked: 'ការបញ្ជាទិញនេះលែងអាចកែបានទៀតហើយ',
    empty_cart: 'កន្ត្រករបស់អ្នកទទេ',
    currency_mismatch: 'រូបិយប័ណ្ណមិនត្រូវគ្នា',
    shipping_unavailable: 'ហាងមិនដឹកជញ្ជូនទៅខេត្តនោះទេ',
    payment_provider_unavailable: 'ការបង់ប្រាក់មិនអាចប្រើបានបណ្ដោះអាសន្ន — សូមព្យាយាមម្ដងទៀត',
    payment_already_confirmed: 'ការបង់ប្រាក់នេះបានបញ្ជាក់រួចហើយ',
    payment_expired: 'កូដបង់ប្រាក់នេះផុតកំណត់ហើយ',
    unique_amount_exhausted: 'មានការបញ្ជាទិញច្រើនពេកក្នុងចំនួនទឹកប្រាក់ដូចគ្នា — សូមព្យាយាមម្ដងទៀត',
    khqr_config_missing: 'ហាងមិនទាន់បញ្ចប់ការរៀបចំការបង់ប្រាក់ទេ',
    plan_required: 'មុខងារនេះត្រូវការគម្រោងខ្ពស់ជាង',
    policy_not_accepted: 'សូមទទួលយកគោលការណ៍វេទិកា មុននឹងបង្ហោះលក់',
    insufficient_credits: 'Credits មិនគ្រប់គ្រាន់',
    quota_exceeded: 'អ្នកបានប្រើដល់ដែនកំណត់ខែនេះហើយ',
    rate_limited: 'សំណើច្រើនពេក — សូមបន្ថយល្បឿន',
    upstream_failed: 'សេវាកម្មដែលយើងពឹងផ្អែកមិនឆ្លើយតបទេ',
    internal: 'មានបញ្ហាបច្ចេកទេសនៅខាងយើង',
  },

  /** §8's onboarding checklist. */
  setup: {
    title: 'បញ្ចប់ការរៀបចំ',
    intro: 'នៅសល់បន្តិចទៀត មុនហាងរបស់អ្នកអាចទទួលការបញ្ជាទិញដំបូង។',
    acceptPolicies: 'ទទួលយកគោលការណ៍វេទិកា',
    acceptPoliciesHint: 'ត្រូវធ្វើមុន ទើបអាចបង្ហោះទំនិញលក់បាន។',
    addProduct: 'បង្ហោះទំនិញដំបូង',
    addProductHint: 'បញ្ចូលដោយដៃ ឬនាំចូលពីប្រកាស Facebook។',
    chooseTheme: 'ជ្រើសរើសរូបរាងហាង',
    chooseThemeHint: 'ទម្រង់ ពណ៌ និងឡូហ្គោរបស់អ្នក។',
    connectBot: 'ភ្ជាប់បូត Telegram',
    connectBotHint: 'អតិថិជនទទួលដំណឹងអំពីការបញ្ជាទិញក្នុង Telegram។ ហាងនៅដំណើរការបានទោះគ្មានវា។',
    paymentDetails: 'រៀបចំវិធីទទួលប្រាក់',
    paymentDetailsHint: 'គណនី ABA សម្រាប់ KHQR ឬបង់ប្រាក់ពេលទទួលទំនិញតែប៉ុណ្ណោះ។',
    viewStorefront: 'មើលហាងរបស់អ្នកដូចអតិថិជនឃើញ',
  },


};
